'use client'

import { z } from 'zod'

import {
  ApiError,
  api,
  runOperation,
  waitOperation,
} from '@/features/veluvit/services/api'
import {
  novelSchema,
  novelListSchema,
} from '@/features/veluvit/schemas/novel.schema'
import type {
  Novel,
  NovelSummary,
} from '@/features/veluvit/schemas/novel.schema'

import type { OperationOptions } from '@/features/veluvit/services/api'

export type NovelLength = 'short' | 'medium' | 'long'
export type NovelTone = 'happy' | 'sad' | 'open' | 'auto'
export const NOVEL_LENGTHS: Record<
  NovelLength,
  { label: string; approx: string }
> = {
  short: { label: '짧게', approx: '약 1,500~3,000자' },
  medium: { label: '중간', approx: '약 3,500~6,000자' },
  long: { label: '길게', approx: '약 7,000~10,000자' },
}
export const NOVEL_LENGTH_ORDER: NovelLength[] = ['short', 'medium', 'long']
export const NOVEL_TONES: { v: NovelTone; label: string }[] = [
  { v: 'happy', label: '해피' },
  { v: 'sad', label: '새드' },
  { v: 'open', label: '열린 결말' },
  { v: 'auto', label: 'AI에게 맡기기' },
]
export const NOVEL_TITLE_MAX = 20

export type { Novel } from '@/features/veluvit/schemas/novel.schema'
export type NovelInput = {
  title: string
  length: NovelLength
  tone: NovelTone
  cover?: string
}

const exportConfigSchema = z.object({
  exports: z.object({
    novel: z.object({
      enabled: z.boolean(),
      pricingConfigured: z.boolean(),
      testFree: z.boolean(),
      prices: z.object({
        short: z.number().int().nonnegative().optional(),
        medium: z.number().int().nonnegative().optional(),
        long: z.number().int().nonnegative().optional(),
      }),
      lengths: z.array(z.enum(['short', 'medium', 'long'])),
      tones: z.array(z.enum(['happy', 'sad', 'open', 'auto'])),
    }),
  }),
})
type NovelExportConfig = z.infer<typeof exportConfigSchema>['exports']['novel']
export function novelExportConfig(config: unknown): NovelExportConfig | null {
  const parsed = exportConfigSchema.safeParse(config)
  return parsed.success ? parsed.data.exports.novel : null
}

function readNovel(value: unknown): Novel {
  const parsed = novelSchema.safeParse(value)
  if (!parsed.success)
    throw new ApiError('소설 응답을 확인하지 못했어요.', 502, 'INVALID_NOVEL')
  return parsed.data
}

export async function createNovel(
  roomId: string,
  input: NovelInput,
  operationRequestId: string,
  options: OperationOptions = {}
): Promise<Novel> {
  const result = await runOperation<unknown>(
    `/room/${encodeURIComponent(roomId)}/novel/`,
    {
      requestId: operationRequestId,
      title: input.title.trim(),
      length: input.length,
      tone: input.tone,
    },
    options
  )
  return readNovel(result)
}

export async function resumeNovel(
  operationId: string,
  options: OperationOptions = {}
): Promise<Novel> {
  return readNovel(await waitOperation<unknown>(operationId, options))
}

export async function listNovels(): Promise<NovelSummary[]> {
  const result = await api<unknown>('/novels/')
  const parsed = novelListSchema.safeParse(result)
  if (!parsed.success)
    throw new ApiError(
      '서재 응답을 확인하지 못했어요.',
      502,
      'INVALID_NOVEL_LIST'
    )
  return parsed.data.novels
}

export async function getNovel(id: string): Promise<Novel | null> {
  try {
    return readNovel(await api<unknown>(`/novels/${encodeURIComponent(id)}/`))
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null
    throw error
  }
}

export async function deleteNovel(id: string): Promise<void> {
  await api(`/novels/${encodeURIComponent(id)}/`, undefined, 'DELETE')
}
